import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import MuiDialog from '@mui/material/Dialog';
import { useDataProps, useAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { closeModalOnEscapeKeyDown } from './close-modal-on-escape-key-down';
import { triggerModalPrimaryAction } from './trigger-modal-primary-action';
// Grid template areas for different variants
const gridTemplateAreaContextual = `
    "header header"
    "left right"
    "left footer"
`;
const gridTemplateAreaFull = `
    "header header"
    "left right"
    "footer footer"
`;
const cardPaperSx = (theme) => {
    var _a;
    return ({
        height: 'fit-content',
        maxHeight: {
            base: '100%',
            mobileLandscape: '90vh',
        },
        minWidth: {
            mobileLandscape: 'min(700px, 100%)',
        },
        maxWidth: {
            mobileLandscape: 'min(70vw, 1440px)',
        },
        borderRadius: '16px',
        boxShadow: (_a = theme.modernFamlyTheme) === null || _a === void 0 ? void 0 : _a.elevation[4],
    });
};
// MUI's own `fullScreen` sizes the paper to the viewport; only the card's elevation is left to remove.
const fullScreenPaperSx = {
    boxShadow: 'none',
};
export const SplitModalContext = React.createContext(undefined);
/**
 * Split modal component that renders content in a grid layout with left and right sections.
 *
 * The modal uses CSS Grid with predefined areas that vary by variant:
 *
 * **Contextual variant:**
 * - header: spans across both columns
 * - left: main content area (primary content, forms, data)
 * - right: contextual content area, grey (n50) by default or optionally white (sidebar, actions)
 * - footer: sticky footer in right column area
 *
 * **FullModal variant:**
 * - header: spans across both columns
 * - left: main content area (primary content, forms, data)
 * - right: contextual content area, grey (n50) by default or optionally white (sidebar, actions)
 * - footer: spans across both columns at the bottom (automatic positioning: single element on right, multiple elements flow naturally)
 *
 * **Dimensions:**
 * - Min width: 700px, capped to the viewport width
 * - Max width: 70% of viewport (capped at 1440px breakpoint)
 * - Height: fits its content, up to 90% of viewport
 * - With `fullScreen`: fills the viewport at every breakpoint, without rounded corners or shadow
 * - Content areas: Scrollable when content overflows, footer always stays at bottom
 *
 * @param open - Whether the modal should be open
 * @param variant - The variant of the split modal ('contextual' | 'fullModal')
 * @param gridTemplateColumns - CSS grid-template-columns value (defaults to '1fr 1fr')
 * @param onClose - Called when the user dismisses the modal with the `Escape` key
 * @param children - React children that will be rendered in the grid layout
 *
 * @example
 * **CONTEXTUAL VARIANT USAGE:**
 * ```
 * <SplitModal variant="contextual" gridTemplateColumns="2fr 1fr">
 *   <SplitModalHeader>
 *     <ModalTitle title="Form Title" />
 *     <ModalCloseButton onClose={onClose} />
 *   </SplitModalHeader>
 *   <SplitModalContentLeft>Main content (forms, data, primary UI)</SplitModalContentLeft>
 *   <SplitModalContentRight>
 *     Contextual content (sidebar, actions, summary)
 *   </SplitModalContentRight>
 *   <SplitModalFooter>
 *     <SplitModalFooterButtons>
 *       <ModalDeleteButton text="Delete" onClick={handleDelete} />
 *       <ModalSecondaryButton text="Cancel" onClick={onClose} />
 *       <ModalPrimaryButton text="Save" onClick={handleSave} />
 *     </SplitModalFooterButtons>
 *   </SplitModalFooter>
 * </SplitModal>
 * ```
 *
 * @example
 * **FULLMODAL VARIANT USAGE:**
 * ```tsx
 * // Just buttons (automatically positioned on the right)
 * <SplitModal variant="fullModal" gridTemplateColumns="3fr 1fr">
 *   <SplitModalHeader>
 *     <ModalTitle title="Settings" />
 *     <ModalCloseButton onClose={onClose} />
 *   </SplitModalHeader>
 *   <SplitModalContentLeft>Main content (primary forms, data)</SplitModalContentLeft>
 *   <SplitModalContentRight>Contextual content (sidebar, help)</SplitModalContentRight>
 *   <SplitModalFooter>
 *     <SplitModalFooterButtons>
 *         <ModalDeleteButton text="Delete" onClick={handleDelete} />
 *         <ModalSecondaryButton text="Cancel" onClick={onClose} />
 *         <ModalPrimaryButton text="Save" onClick={handleSave} />
 *     </SplitModalFooterButtons>
 *   </SplitModalFooter>
 * </SplitModal>
 *
 * // With optional description (description left, buttons right - automatic!)
 * <SplitModal variant="fullModal" gridTemplateColumns="3fr 1fr">
 *   <SplitModalHeader>
 *     <ModalTitle title="Settings" />
 *     <ModalCloseButton onClose={onClose} />
 *   </SplitModalHeader>
 *   <SplitModalContentLeft>Main content (primary forms, data)</SplitModalContentLeft>
 *   <SplitModalContentRight>Contextual content (sidebar, help)</SplitModalContentRight>
 *   <SplitModalFooter>
 *     <ModalDescription description="Optional footer description or warning" />
 *     <SplitModalFooterButtons>
 *         <ModalDeleteButton text="Delete" onClick={handleDelete} />
 *         <ModalSecondaryButton text="Cancel" onClick={onClose} />
 *         <ModalPrimaryButton text="Save" onClick={handleSave} />
 *     </SplitModalFooterButtons>
 *   </SplitModalFooter>
 * </SplitModal>
 * ```
 *
 * @remarks
 * **IMPORTANT:** You must use the provided styled components (`SplitModalHeader`, `SplitModalContentLeft`, `SplitModalContentRight`, `SplitModalFooter`, `SplitModalFooterButtons`)
 * to position the content in the grid. The CSS styling (grey background, padding, borders) is applied automatically
 * based on the variant.
 *
 * **FULLMODAL FOOTER LAYOUT:** The footer automatically positions content using CSS Grid:
 * - **Single element** (`SplitModalFooterButtons`): Automatically positioned on the right
 * - **Two elements** (description + `SplitModalFooterButtons`): Description goes left, buttons go right automatically
 * - **No manual styling needed** - the positioning happens automatically based on the variant.
 *
 * **MODAL COMPONENTS:** Use the modal components for consistent styling:
 * - `SplitModalHeader` with `ModalTitle` and `ModalCloseButton` for the header area
 * - `SplitModalFooter` with `SplitModalFooterButtons` for the footer area
 * - `ModalPrimaryButton`, `ModalSecondaryButton`, and `ModalDeleteButton` for actions
 */
export const SplitModal = ({ open, variant, gridTemplateColumns = '1fr 1fr', fullScreen = false, onClose, children, ...rest }) => {
    const dataAttributes = useDataProps(rest);
    const ariaAttributes = useAriaProps(rest);
    // `null` until the user switches with a `ModalFullScreenButton`, so the prop decides until then.
    const [chosenFullScreen, setFullScreen] = React.useState(null);
    const isFullScreen = chosenFullScreen !== null && chosenFullScreen !== void 0 ? chosenFullScreen : fullScreen;
    const isContextual = variant === 'contextual';
    const gridTemplateAreas = isContextual ? gridTemplateAreaContextual : gridTemplateAreaFull;
    return (_jsx(SplitModalContext, { value: { variant, fullScreen: isFullScreen, setFullScreen }, children: _jsx(MuiDialog, { open: open, fullScreen: isFullScreen, onClose: closeModalOnEscapeKeyDown(onClose), onKeyDown: triggerModalPrimaryAction, ...dataAttributes, ...ariaAttributes, PaperProps: {
                sx: theme => {
                    const paperSizeSx = isFullScreen ? fullScreenPaperSx : cardPaperSx(theme);
                    return {
                        width: '100%',
                        ...paperSizeSx,
                        margin: 0,
                        padding: 0,
                        overflow: 'hidden',
                        display: 'grid',
                        gridTemplateColumns,
                        gridTemplateAreas,
                        gridTemplateRows: isContextual ? 'auto 1fr' : 'auto 1fr auto',
                    };
                },
            }, children: children }) }));
};
